import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { ReportField, ReportPreset } from '~/utils/types/reportPreset';

export const fetchReportFields = async (
  reportType: string
): Promise<ReportField[]> => {
  const { data } = await instance.get(
    `admin/report-presets/fields/${reportType}`
  );
  return data;
};

export const fetchReportPresets = async (
  reportType: string
): Promise<ReportPreset[]> => {
  const { data } = await instance.get('admin/report-presets', {
    params: { report_type: reportType },
  });
  return data;
};

export const createReportPreset = async (payload: {
  report_type: string;
  name: string;
  fields: string[];
  [key: string]: unknown;
}): Promise<ReportPreset> => {
  const { data } = await instance.post('admin/report-presets', payload);
  return data;
};

export const deleteReportPreset = async (id: number): Promise<void> => {
  await instance.delete(`admin/report-presets/${id}`);
};

export const useReportFields = (reportType: string) =>
  useQuery({
    queryKey: ['reportFields', reportType],
    queryFn: () => fetchReportFields(reportType),
    enabled: !!reportType,
  });

export const useReportPresets = (reportType: string) =>
  useQuery({
    queryKey: ['reportPresets', reportType],
    queryFn: () => fetchReportPresets(reportType),
    enabled: !!reportType,
  });

export const useCreatePreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createReportPreset,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['reportPresets', variables.report_type],
      });
    },
  });
};

export const useDeletePreset = (reportType: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteReportPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reportPresets', reportType],
      });
    },
  });
};
